import { config } from "@/config";

type TitleDescriptionItem = {
  title: string;
  description: string;
};

type FlowStep = {
  label: string;
  title: string;
  description: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

const PROBLEM_ITEMS: readonly TitleDescriptionItem[] = [
  {
    title: "Forms are hard to decode",
    description:
      "Immigration, legal, and tax PDFs use dense field labels that are easy to misread.",
  },
  {
    title: "Language adds friction",
    description: "Users often work with forms in a language that is not their strongest language.",
  },
  {
    title: "Switching tools wastes time",
    description:
      "People jump between search, chat, and PDF editors instead of finishing in one flow.",
  },
];

const FLOW_STEPS: readonly FlowStep[] = [
  {
    label: "Step 1",
    title: "Upload one AcroForm PDF",
    description: "Start with a fillable PDF form. The app validates it before continuing.",
  },
  {
    label: "Step 2",
    title: "Ask and fill in chat",
    description:
      "Ask what fields mean, share your details, and let AI update form fields through tools.",
  },
  {
    label: "Step 3",
    title: "Download the filled file",
    description: "Review the result and export a filled PDF for your final check and submission.",
  },
];

const CAPABILITY_ITEMS: readonly TitleDescriptionItem[] = [
  {
    title: "Field explanations",
    description: "Get plain-language guidance for each field and the form intent.",
  },
  {
    title: "Tool-based form filling",
    description:
      "AI writes values to actual fields instead of only suggesting text in a separate chat.",
  },
  {
    title: "Language-flexible chat",
    description: "The UI stays in English while AI can respond in the user language.",
  },
  {
    title: "Live form context",
    description: "The assistant uses your uploaded form schema so answers stay tied to your PDF.",
  },
];

const TRUST_ITEMS: readonly TitleDescriptionItem[] = [
  {
    title: "No account required",
    description: "Open the app and start immediately without sign-up or onboarding friction.",
  },
  {
    title: "Built for sensitive forms",
    description:
      "The workflow is designed for session-based processing with clear boundaries around storage.",
  },
  {
    title: "Form-scoped AI behavior",
    description:
      "The assistant is instructed to stay focused on your uploaded form and refuse off-topic prompts.",
  },
];

const FAQ_ITEMS: readonly FaqItem[] = [
  {
    question: "What PDF files are supported?",
    answer:
      "PDFPilot supports fillable AcroForm PDFs. Scanned or flat PDFs without form fields are not supported in this MVP.",
  },
  {
    question: "Can I upload multiple PDFs at once?",
    answer:
      "No. The MVP keeps one active PDF session at a time to keep context clear and predictable.",
  },
  {
    question: "Can AI answer unrelated questions?",
    answer:
      "No. The assistant is designed to focus on your uploaded form and related field-level help.",
  },
  {
    question: "Does this work on mobile?",
    answer: "Landing is responsive, but the product workflow is currently focused on desktop use.",
  },
];

const CURRENT_YEAR: number = new Date().getFullYear();

const BEST_FIT_POINTS: readonly string[] = [
  "One fillable AcroForm PDF at a time",
  "Text, checkbox, radio, and dropdown field support",
  "AI guidance linked to the uploaded form",
];

const CURRENT_LIMIT_POINTS: readonly string[] = [
  "No support for scanned non-fillable PDFs",
  "No multi-file workflows in one session",
  "Product experience focused on desktop usage",
];

const LANDING_BACKGROUND_STYLE: React.CSSProperties = {
  backgroundImage:
    "radial-gradient(circle at 15% -5%, color-mix(in oklab, var(--color-primary) 22%, transparent) 0%, transparent 45%), radial-gradient(circle at 90% 0%, color-mix(in oklab, var(--color-secondary) 28%, transparent) 0%, transparent 38%), linear-gradient(180deg, color-mix(in oklab, var(--color-card) 70%, transparent) 0%, transparent 55%)",
};

const GRID_OVERLAY_STYLE: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(to right, color-mix(in oklab, var(--color-foreground) 10%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in oklab, var(--color-foreground) 10%, transparent) 1px, transparent 1px)",
  backgroundSize: "44px 44px",
};

const HERO_PANEL_STYLE: React.CSSProperties = {
  background:
    "linear-gradient(160deg, color-mix(in oklab, var(--color-card) 88%, transparent) 0%, color-mix(in oklab, var(--color-muted) 84%, transparent) 100%)",
};

const CONTENT_CARD_CLASS_NAME = "rounded-2xl border border-border/70 p-5";
const SOFT_CONTENT_CARD_CLASS_NAME = `${CONTENT_CARD_CLASS_NAME} bg-card/70`;
const ELEVATED_CONTENT_CARD_CLASS_NAME = `${CONTENT_CARD_CLASS_NAME} bg-card/75`;

type SectionHeadingProps = {
  eyebrow: string;
  title: string;
  description: string;
  align?: "left" | "center";
  id?: string;
};

function SectionHeading({
  eyebrow,
  title,
  description,
  align = "left",
  id,
}: SectionHeadingProps): React.JSX.Element {
  const alignmentClassName: string = align === "center" ? "text-center" : "text-left";

  return (
    <div id={id} className={`space-y-3 ${alignmentClassName}`}>
      <p className="text-xs font-semibold tracking-[0.22em] text-primary uppercase">{eyebrow}</p>
      <h2 className="font-serif text-3xl leading-tight font-bold tracking-tight text-foreground sm:text-4xl">
        {title}
      </h2>
      <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
        {description}
      </p>
    </div>
  );
}

type TitleDescriptionCardProps = {
  title: string;
  description: string;
  className: string;
};

function TitleDescriptionCard({
  title,
  description,
  className,
}: TitleDescriptionCardProps): React.JSX.Element {
  return (
    <article className={className}>
      <p className="text-base font-semibold text-foreground">{title}</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>
    </article>
  );
}

type FlowStepCardVariant = "hero" | "section";

type FlowStepCardProps = {
  step: FlowStep;
  variant: FlowStepCardVariant;
};

function FlowStepCard({ step, variant }: FlowStepCardProps): React.JSX.Element {
  if (variant === "hero") {
    return (
      <div className="rounded-2xl border border-border/60 bg-background/75 p-4">
        <p className="text-[11px] font-semibold tracking-[0.16em] text-primary uppercase">
          {step.label}
        </p>
        <p className="mt-1 text-sm font-semibold text-foreground">{step.title}</p>
        <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{step.description}</p>
      </div>
    );
  }

  return (
    <article className="rounded-2xl border border-border/70 bg-card/75 p-5">
      <p className="text-[11px] font-semibold tracking-[0.16em] text-primary uppercase">
        {step.label}
      </p>
      <p className="mt-2 text-base font-semibold text-foreground">{step.title}</p>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.description}</p>
    </article>
  );
}

type DashListProps = {
  items: readonly string[];
};

function DashList({ items }: DashListProps): React.JSX.Element {
  return (
    <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
      {items.map((item: string) => (
        <li key={item}>- {item}</li>
      ))}
    </ul>
  );
}

export function LandingPage(): React.JSX.Element {
  return (
    <main className="relative min-h-screen overflow-x-hidden bg-background text-foreground">
      <div className="pointer-events-none absolute inset-0" style={LANDING_BACKGROUND_STYLE} />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={GRID_OVERLAY_STYLE}
      />

      <div className="relative z-10 mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <header className="py-5 sm:py-7">
          <div className="flex items-center justify-between rounded-full border border-border/70 bg-card/80 px-4 py-2.5 shadow-sm backdrop-blur-sm sm:px-6">
            <a href="/" className="font-sans text-sm font-semibold tracking-[0.16em] uppercase">
              PDFPilot
            </a>

            <nav className="hidden items-center gap-6 text-sm text-muted-foreground md:flex">
              <a href="#how-it-works" className="transition-colors hover:text-foreground">
                How it works
              </a>
              <a href="#privacy" className="transition-colors hover:text-foreground">
                Privacy
              </a>
              <a href="#faq" className="transition-colors hover:text-foreground">
                FAQ
              </a>
            </nav>

            <a
              href={config.appBaseUrl}
              className="rounded-full bg-primary px-4 py-2 text-xs font-semibold tracking-wide text-primary-foreground uppercase transition-opacity hover:opacity-90"
            >
              Open App
            </a>
          </div>
        </header>

        <section className="grid gap-8 pb-18 pt-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center lg:gap-12">
          <div
            className="space-y-6 animate-in fade-in slide-in-from-left-5"
            style={{ animationDuration: "620ms" }}
          >
            <p className="inline-flex rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold tracking-[0.18em] text-primary uppercase">
              AI PDF Form Assistant
            </p>
            <h1 className="font-serif text-4xl leading-[1.05] font-bold tracking-tight text-balance sm:text-5xl lg:text-6xl">
              Understand and fill PDF forms in one clean flow.
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              Upload a fillable PDF, ask AI what each field means, fill values in chat, and download
              a completed file. No account setup.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <a
                href={config.appBaseUrl}
                className="rounded-full bg-primary px-6 py-3 text-sm font-semibold tracking-wide text-primary-foreground uppercase transition-transform duration-200 hover:-translate-y-0.5"
              >
                Start with your PDF
              </a>
              <a
                href="#how-it-works"
                className="rounded-full border border-border bg-card/70 px-6 py-3 text-sm font-semibold tracking-wide text-foreground uppercase transition-colors hover:bg-card"
              >
                See the 3-step flow
              </a>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <p className="rounded-xl border border-border/60 bg-card/70 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                Built for immigration, legal, and tax forms.
              </p>
              <p className="rounded-xl border border-border/60 bg-card/70 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                AI stays scoped to your uploaded form context.
              </p>
              <p className="rounded-xl border border-border/60 bg-card/70 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                Desktop-first workflow for focused completion.
              </p>
            </div>
          </div>

          <aside
            className="relative overflow-hidden rounded-3xl border border-border/70 p-6 shadow-xl shadow-primary/10 animate-in fade-in slide-in-from-right-5"
            style={{ ...HERO_PANEL_STYLE, animationDuration: "620ms", animationDelay: "120ms" }}
          >
            <div className="pointer-events-none absolute -right-14 -top-14 h-40 w-40 rounded-full bg-primary/15 blur-2xl" />
            <p className="text-xs font-semibold tracking-[0.2em] text-muted-foreground uppercase">
              Session Snapshot
            </p>
            <div className="mt-5 space-y-4">
              {FLOW_STEPS.map((step: FlowStep) => (
                <FlowStepCard key={step.label} step={step} variant="hero" />
              ))}
            </div>
          </aside>
        </section>

        <section className="space-y-6 pb-18">
          <SectionHeading
            eyebrow="Why PDFPilot"
            title="The real bottleneck is not the PDF editor."
            description="People lose time understanding fields and context switching between tools. PDFPilot keeps explanation and filling together."
          />
          <div className="grid gap-4 md:grid-cols-3">
            {PROBLEM_ITEMS.map((item: TitleDescriptionItem) => (
              <TitleDescriptionCard
                key={item.title}
                title={item.title}
                description={item.description}
                className={SOFT_CONTENT_CARD_CLASS_NAME}
              />
            ))}
          </div>
        </section>

        <section id="how-it-works" className="space-y-6 pb-18">
          <SectionHeading
            eyebrow="How It Works"
            title="Three steps from upload to completed PDF"
            description="Each step removes friction so users can move from confusion to a usable final file quickly."
          />
          <div className="grid gap-4 md:grid-cols-3">
            {FLOW_STEPS.map((step: FlowStep) => (
              <FlowStepCard key={step.title} step={step} variant="section" />
            ))}
          </div>
        </section>

        <section className="space-y-6 pb-18">
          <SectionHeading
            eyebrow="Capabilities"
            title="What users can do today"
            description="Focused feature scope for practical completion, not broad document editing."
          />
          <div className="grid gap-4 md:grid-cols-2">
            {CAPABILITY_ITEMS.map((item: TitleDescriptionItem) => (
              <TitleDescriptionCard
                key={item.title}
                title={item.title}
                description={item.description}
                className={ELEVATED_CONTENT_CARD_CLASS_NAME}
              />
            ))}
          </div>
        </section>

        <section id="privacy" className="space-y-6 pb-18">
          <SectionHeading
            eyebrow="Trust"
            title="Designed for sensitive form workflows"
            description="People use PDFPilot for personal data-heavy documents, so transparency and clear constraints are part of the product."
          />
          <div className="grid gap-4 md:grid-cols-3">
            {TRUST_ITEMS.map((item: TitleDescriptionItem) => (
              <TitleDescriptionCard
                key={item.title}
                title={item.title}
                description={item.description}
                className={ELEVATED_CONTENT_CARD_CLASS_NAME}
              />
            ))}
          </div>
          <a
            href="/privacy"
            className="inline-flex rounded-full border border-border bg-card/80 px-5 py-2.5 text-xs font-semibold tracking-[0.15em] text-foreground uppercase transition-colors hover:bg-card"
          >
            Read Privacy Policy
          </a>
        </section>

        <section className="grid gap-6 rounded-3xl border border-border/70 bg-card/70 p-6 pb-18 md:grid-cols-2">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              Best Fit
            </p>
            <h3 className="mt-2 font-serif text-2xl font-bold tracking-tight">
              Use PDFPilot when you need speed and clarity.
            </h3>
            <DashList items={BEST_FIT_POINTS} />
          </div>
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              Current Limits
            </p>
            <h3 className="mt-2 font-serif text-2xl font-bold tracking-tight">
              Narrow scope keeps the MVP reliable.
            </h3>
            <DashList items={CURRENT_LIMIT_POINTS} />
          </div>
        </section>

        <section id="faq" className="space-y-6 pb-18 pt-18">
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently asked questions"
            description="Short answers for the most common launch-stage concerns."
          />
          <div className="space-y-3">
            {FAQ_ITEMS.map((item: FaqItem) => (
              <TitleDescriptionCard
                key={item.question}
                title={item.question}
                description={item.answer}
                className={ELEVATED_CONTENT_CARD_CLASS_NAME}
              />
            ))}
          </div>
        </section>

        <section className="relative overflow-hidden rounded-3xl border border-border/70 bg-card/80 px-6 py-10 text-center shadow-lg shadow-primary/10">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-primary/15 to-transparent" />
          <div className="relative space-y-4">
            <p className="text-xs font-semibold tracking-[0.22em] text-primary uppercase">
              Ready to start
            </p>
            <h2 className="font-serif text-3xl leading-tight font-bold tracking-tight sm:text-4xl">
              Upload your form and finish it with AI support.
            </h2>
            <p className="mx-auto max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              Start in seconds, keep context in one place, and leave with a filled PDF you can
              review.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <a
                href={config.appBaseUrl}
                className="rounded-full bg-primary px-6 py-3 text-sm font-semibold tracking-[0.14em] text-primary-foreground uppercase transition-opacity hover:opacity-90"
              >
                Open PDFPilot
              </a>
              <a
                href="/privacy"
                className="rounded-full border border-border bg-background/70 px-6 py-3 text-sm font-semibold tracking-[0.14em] text-foreground uppercase"
              >
                Privacy Policy
              </a>
            </div>
          </div>
        </section>

        <footer className="pt-12 text-center text-xs text-muted-foreground">
          <p>PDFPilot - AI assistant for fillable PDF forms.</p>
          <p className="mt-1">
            <a href="/privacy" className="underline-offset-2 hover:underline">
              Privacy
            </a>{" "}
            | {CURRENT_YEAR} PDFPilot
          </p>
        </footer>
      </div>
    </main>
  );
}
